// securityEvidenceHelpers — pure logic (no React) for rendering Microsoft Defender
// `security_alert` events (Graph Security API). These alerts carry the high-signal
// content in an `evidence[]` array; each item has an `@odata.type` like
// `#microsoft.graph.security.fileEvidence`. The shared SourceEventRenderer had no
// `security_alert` view, so these fell through to the generic scalar grid which
// never recurses into `evidence[]` — file name / path / hashes / signer, the
// process command line, the C2 URL, etc. were all invisible.
//
// Two layers (see plan):
//   Layer 1 — EVIDENCE_SPECS: one field renderer per evidence kind, keyed by the
//             short @odata.type. Shared building blocks reused across every family.
//   Layer 2 — FAMILY_PROFILES: per-`category` emphasis — which evidence is the
//             PRIMARY signal (rendered first + expanded + drives the banner) vs
//             CONTEXT (collapsed). This is where Malware ≠ C2 ≠ CredentialAccess.
//
// Field specs return [label, value, opts] tuples — the same shape ScalarGrid
// consumes — where `opts` may carry display flags the EvidenceGrid in
// SourceEventRenderer.jsx interprets: { mono, copy, ip, url, ts, warn, emphasis, clamp }.
// Keeping this file JSX-free means SourceEventRenderer owns all element creation.

// ── Verdict / status tone ────────────────────────────────────────────────────

// malicious > suspicious > other > unknown — drives evidence ordering + banner pick.
export const VERDICT_RANK = { malicious: 3, suspicious: 2, clean: 1, noThreatsFound: 1, unknown: 0 };

export function verdictRank(v) {
  return VERDICT_RANK[v] ?? 0;
}

// Tailwind text color for a verdict. Mirrors the red/amber/green/gray idiom used
// inline in SecurityIncidentView.
export function verdictTextClass(v) {
  if (v === 'malicious') return 'text-red-400';
  if (v === 'suspicious') return 'text-amber-400';
  if (v === 'clean' || v === 'noThreatsFound') return 'text-green-400';
  return 'text-gray-400';
}

// detectionStatus / remediationStatus tone: a stopped threat (prevented/blocked/
// remediated) is reassuring (green-ish); active/detected means it may still be live.
export function statusTextClass(s) {
  const v = (s || '').toLowerCase();
  if (v === 'prevented' || v === 'blocked' || v === 'remediated') return 'text-green-400';
  if (v === 'active' || v === 'detected') return 'text-amber-400';
  return 'text-gray-400';
}

// Defang a URL for safe inline text display (no auto-linking in the banner).
export function defangForDisplay(url) {
  if (!url) return '';
  return String(url).replace(/\./g, '[.]').replace(/^https?:\/\//i, (m) => m.replace('://', '[:]//'));
}

// ── Layer 1: evidence field specs ────────────────────────────────────────────

// `#microsoft.graph.security.fileEvidence` → 'file'
export function evidenceShortType(odataType) {
  return String(odataType || '')
    .replace('#microsoft.graph.security.', '')
    .replace(/Evidence$/, '');
}

// fileDetails (fileEvidence.fileDetails) and processEvidence.imageFile share an
// identical shape, so one helper serves both. Flags an unsigned binary — a real
// red flag, especially for DefenseEvasion DLL-load alerts.
export function fileDetailsFields(fd) {
  if (!fd || typeof fd !== 'object') return [];
  const unsigned = !fd.signer && !fd.filePublisher;
  return [
    ['File name', fd.fileName, { copy: true, emphasis: true }],
    ['Path', fd.filePath, { mono: true, copy: true }],
    ['SHA256', fd.sha256, { mono: true, copy: true }],
    ['SHA1', fd.sha1, { mono: true, copy: true }],
    ['Signature', unsigned ? 'Unsigned' : (fd.signer || fd.filePublisher), unsigned ? { warn: true } : {}],
    ['Issuer', fd.issuer, {}],
    ['Publisher', fd.filePublisher, {}],
    ['Size', fd.fileSize != null ? `${fd.fileSize} bytes` : null, {}],
  ];
}

function userAccountUpn(ua) {
  if (!ua || typeof ua !== 'object') return null;
  return ua.userPrincipalName || (ua.domainName && ua.accountName ? `${ua.domainName}\\${ua.accountName}` : ua.accountName) || null;
}

function userFields(ua) {
  if (!ua || typeof ua !== 'object') return [];
  return [
    ['User', ua.userPrincipalName, { mono: true, copy: true }],
    ['Account', ua.accountName, { mono: true }],
    ['Domain', ua.domainName, {}],
    ['Display name', ua.displayName, {}],
    ['SID', ua.userSid, { mono: true, copy: true }],
    ['Entra user ID', ua.azureAdUserId, { mono: true, copy: true }],
  ];
}

export const EVIDENCE_SPECS = {
  file: {
    label: 'File',
    fields: (e) => fileDetailsFields(e.fileDetails),
  },
  process: {
    label: 'Process',
    // Leads with the image file (the artifact a researcher investigates), then
    // the command line, then identity / hashes / lineage.
    fields: (e) => [
      ['Image file', e.imageFile?.fileName, { copy: true, emphasis: true }],
      ['Image path', e.imageFile?.filePath, { mono: true, copy: true }],
      ['Command line', e.processCommandLine, { mono: true, clamp: true }],
      ['Image SHA256', e.imageFile?.sha256, { mono: true, copy: true }],
      ['Process ID', e.processId != null ? String(e.processId) : null, {}],
      ['Parent PID', e.parentProcessId != null ? String(e.parentProcessId) : null, {}],
      ['Created', e.processCreationDateTime, { ts: true }],
      ['Acting user', userAccountUpn(e.userAccount), { mono: true, copy: true }],
    ],
  },
  user: {
    label: 'User',
    fields: (e) => userFields(e.userAccount),
  },
  ip: {
    label: 'IP',
    fields: (e) => [
      ['IP address', e.ipAddress, { ip: true }],
      ['Country', e.countryLetterCode, {}],
      ['City', e.location?.city, {}],
      ['ISP', e.location?.organization || e.location?.carrier, {}],
    ],
  },
  url: {
    label: 'URL',
    fields: (e) => [
      ['URL', e.url, { url: true, copy: true }],
    ],
  },
  mailbox: {
    label: 'Mailbox',
    // Defender for Office 365 mailbox alerts (permission change, forwarding rule,
    // suspicious access). The high-signal field is the mailbox/primaryAddress; the
    // rest of the Graph payload is usually sparse. `userAccount.userPrincipalName`
    // frequently duplicates primaryAddress, so only surface it when it differs.
    fields: (e) => {
      const acct = userAccountUpn(e.userAccount);
      const roles = Array.isArray(e.roles) && e.roles.length ? e.roles.join(', ') : null;
      return [
        ['Mailbox', e.primaryAddress || e.upn, { mono: true, copy: true, emphasis: true }],
        ['UPN', e.upn && e.upn !== e.primaryAddress ? e.upn : null, { mono: true, copy: true }],
        ['Display name', e.displayName, {}],
        ['Account', acct && acct !== e.primaryAddress && acct !== e.upn ? acct : null, { mono: true }],
        ['Roles', roles, {}],
      ];
    },
  },
  device: {
    label: 'Device',
    fields: (e) => [
      ['Host', e.hostName || e.deviceDnsName, { copy: true }],
      ['DNS name', e.deviceDnsName, { mono: true }],
      ['OS', [e.osPlatform, e.version].filter(Boolean).join(' '), {}],
      ['Risk score', e.riskScore, {}],
      ['Health', e.healthStatus, {}],
      ['Internal IP', e.lastIpAddress, { ip: true }],
      ['External IP', e.lastExternalIpAddress, { ip: true }],
      ['Onboarding', e.onboardingStatus, {}],
      ['Defender AV', e.defenderAvStatus, {}],
      ['MDE device ID', e.mdeDeviceId, { mono: true, copy: true }],
    ],
  },
};

// Generic spec for an unrecognized @odata.type — never drop evidence silently.
export function genericEvidenceSpec(shortType) {
  return {
    label: shortType ? `${shortType.charAt(0).toUpperCase()}${shortType.slice(1)}` : 'Evidence',
    fields: (e) => Object.entries(e)
      .filter(([k, v]) => k !== '@odata.type' && v != null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
      .slice(0, 12)
      .map(([k, v]) => [k, String(v), {}]),
  };
}

// ── Layer 2: family profiles ─────────────────────────────────────────────────

// `primary` = evidence types rendered first + expanded + eligible for the banner
// headline. Order within `primary` is the headline preference. Everything else is
// context (collapsed). A new Defender variant = one entry here.
export const FAMILY_PROFILES = {
  Malware:           { primary: ['file'] },
  Execution:         { primary: ['process', 'file'] },
  CommandAndControl: { primary: ['url', 'ip', 'process'] },  // URL leads
  DefenseEvasion:    { primary: ['file', 'process'] },
  CredentialAccess:  { primary: ['process', 'user'] },
};

// Verdict-first fallback for any category not yet profiled.
export const DEFAULT_PROFILE = { primary: ['file', 'url', 'ip', 'process'] };

export function familyProfile(category) {
  return FAMILY_PROFILES[category] || DEFAULT_PROFILE;
}

// ── Parse ─────────────────────────────────────────────────────────────────────

function normalizeEvidence(raw) {
  let arr = raw && raw.evidence;
  // PowerShell ConvertTo-Json can unwrap a single-element array to an object.
  if (!arr) return [];
  if (!Array.isArray(arr)) arr = [arr];
  return arr.filter(Boolean).map((e) => {
    const shortType = evidenceShortType(e['@odata.type']);
    return {
      shortType,
      spec: EVIDENCE_SPECS[shortType] || genericEvidenceSpec(shortType),
      verdict: e.verdict || 'unknown',
      detectionStatus: e.detectionStatus || '',
      remediationStatus: e.remediationStatus || '',
      raw: e,
    };
  });
}

// Returns { header, primary[], context[], all[] }. Primary/context split is per
// the matched family profile; cards only ever exist for evidence that's present,
// so a Malware alert (no url evidence) never yields a URL card.
export function parseSecurityAlert(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const all = normalizeEvidence(raw);
  const profile = familyProfile(raw.category);

  const primary = [];
  const claimed = new Set();
  // Pull primary types in the profile's declared order; within a type, worst verdict first.
  for (const t of profile.primary) {
    const matches = all
      .filter((ev, i) => ev.shortType === t && !claimed.has(i))
      .sort((a, b) => verdictRank(b.verdict) - verdictRank(a.verdict));
    all.forEach((ev, i) => { if (ev.shortType === t) claimed.add(i); });
    primary.push(...matches);
  }
  const context = all.filter((_, i) => !claimed.has(i));

  const header = {
    title: raw.title,
    severity: raw.severity,
    category: raw.category,
    status: raw.status,
    determination: raw.determination,
    detectionSource: raw.detectionSource,
    serviceSource: raw.serviceSource,
    productName: raw.productName,
    firstActivityDateTime: raw.firstActivityDateTime,
    lastActivityDateTime: raw.lastActivityDateTime,
    threatFamily: raw.threatFamilyName || raw.threatDisplayName,
    description: raw.description,
    recommendedActions: raw.recommendedActions,
    mitreTechniques: Array.isArray(raw.mitreTechniques) ? raw.mitreTechniques : [],
    alertWebUrl: raw.alertWebUrl,
  };

  return { header, primary, context, all };
}

// Defender `security_alert` pass-through alerts (e.g. Entra ID Protection
// password-spray) keep the IP + location inside the `ipEvidence` evidence card;
// the detection pipeline never promotes them to the alert-row client_ip /
// ip_country columns, so the portal's IP Intelligence panel reads blank. Lift the
// IP / country / city out of the evidence so the panel can render and the analyst
// can enrich the IP on demand. Returns null when there's no ipEvidence.
export function securityAlertIpEvidence(parsed) {
  if (!parsed) return null;
  const ipEv = parsed.all.find((ev) => ev.shortType === 'ip' && ev.raw && ev.raw.ipAddress);
  if (!ipEv) return null;
  const r = ipEv.raw;
  return {
    ip: r.ipAddress,
    country: r.countryLetterCode || null,
    city: (r.location && r.location.city) || null,
    isp: (r.location && (r.location.organization || r.location.carrier)) || null,
  };
}

// First initiating process's image file — the "related file" a researcher starts
// on when the headline isn't itself a file (network-led C2 alerts).
function relatedProcessFile(parsed) {
  const proc = parsed.all.find((ev) => ev.shortType === 'process' && ev.raw.imageFile?.fileName);
  if (!proc) return null;
  return { fileName: proc.raw.imageFile.fileName, filePath: proc.raw.imageFile.filePath };
}

// Banner headline. Built from the lead primary item (already profile-ordered) so
// the copy is family-appropriate: file for Malware, URL+IP for C2, process+user
// for CredentialAccess. When the lead isn't a file, append the related process
// image file so the researcher always has a file to start on. Returns null when
// there's no meaningful evidence to surface.
export function topEvidenceHighlight(parsed) {
  if (!parsed) return null;
  const lead = parsed.primary[0]
    || [...parsed.all].sort((a, b) => verdictRank(b.verdict) - verdictRank(a.verdict))[0];
  if (!lead) return null;

  const status = lead.remediationStatus || lead.detectionStatus;
  const e = lead.raw;
  let headline = '';
  let relatedFile = null;

  switch (lead.shortType) {
    case 'file': {
      const fd = e.fileDetails || {};
      headline = `File: ${fd.fileName || 'unknown'}`;
      if (fd.filePath) headline += ` — ${fd.filePath}`;
      break;
    }
    case 'url': {
      const destIp = parsed.all.find((ev) => ev.shortType === 'ip')?.raw?.ipAddress;
      headline = `Network blocked: ${defangForDisplay(e.url)}`;
      if (destIp) headline += ` → ${destIp}`;
      relatedFile = relatedProcessFile(parsed);
      break;
    }
    case 'ip': {
      headline = `Network: ${e.ipAddress || 'unknown'}`;
      relatedFile = relatedProcessFile(parsed);
      break;
    }
    case 'process': {
      const img = e.imageFile?.fileName || 'unknown';
      const upn = userAccountUpn(e.userAccount);
      headline = `Process: ${img}${upn ? ` (acting as ${upn})` : ''}`;
      break;
    }
    case 'user': {
      headline = `User: ${userAccountUpn(e.userAccount) || 'unknown'}`;
      relatedFile = relatedProcessFile(parsed);
      break;
    }
    default: {
      headline = `${lead.spec.label}`;
      relatedFile = relatedProcessFile(parsed);
    }
  }

  if (status) headline += ` · ${status}`;
  if (relatedFile && relatedFile.fileName) {
    headline += ` · file: ${relatedFile.fileName}`;
    if (relatedFile.filePath) headline += ` (${relatedFile.filePath})`;
  }

  // "+N more" — other evidence items that aren't clean/unknown.
  const moreCount = parsed.all.filter((ev) => ev !== lead && verdictRank(ev.verdict) >= 2).length;

  return {
    verdict: lead.verdict,
    tone: lead.verdict === 'malicious' ? 'critical' : lead.verdict === 'suspicious' ? 'warning' : 'neutral',
    headline,
    moreCount,
  };
}
